import { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { BRAND, BRAND_LIGHT } from "@/shared/constants.js";
import { Field } from "@/shared/components/Field.jsx";
import { TextInput } from "@/shared/components/TextInput.jsx";
import { EyeIcon, UserIcon } from "@/shared/components/Icon.jsx";
import Alert from "@/shared/components/Alert.jsx";
import { btnPrimary, dis } from "@/shared/utils/styles.js";

import {
  fetchGoogleClientId,
  fetchInsuredSession,
  postSignIn,
  postSignInWithGoogle,
} from "@/modules/Auth/api/authApi.js";
import { loadGsiScript } from "@/modules/Auth/utils/google.js";
import { isInsuredSessionPayload } from "@/modules/Quote/utils/submission.js";

import { MedMalGuardHeader } from "@/modules/Quote/components/MedMalGuardLanding.jsx";

import { useStore } from "@/shared/store/useStore.js";
import loginStore from "@/modules/Auth/store/loginStore.js";
import sessionStore from "@/shared/store/sessionStore.js";
import insuredProfileStore from "@/shared/store/insuredProfileStore.js";
import ilfDlfStore from "@/modules/Quote/store/ilfDlfStore.js";
import specialityStore from "@/modules/Quote/store/specialityStore.js";
import quoteFormStore from "@/modules/Quote/store/quoteFormStore.js";
import personalStore from "@/modules/Quote/store/personalStore.js";
import classificationStore from "@/modules/Quote/store/classificationStore.js";
import coverageStore from "@/modules/Quote/store/coverageStore.js";
import questionsStore from "@/modules/Quote/store/questionsStore.js";
import submissionStore from "@/modules/Quote/store/submissionStore.js";
import attestStore from "@/modules/Quote/store/attestStore.js";

export default function SignInPage() {
  const navigate = useNavigate();
  const [googleSignInLoading, setGoogleSignInLoading] = useState(false);

  // ── Store-backed reads ─────────────────────────────────────────────────
  const loginEmail      = useStore(loginStore, (s) => s.loginEmail);
  const loginPassword   = useStore(loginStore, (s) => s.loginPassword);
  const signInLoading   = useStore(loginStore, (s) => s.signInLoading);
  const signInError     = useStore(loginStore, (s) => s.signInError);
  const sessionReady    = useStore(sessionStore, (s) => s.sessionReady);
  const insuredProfile  = useStore(insuredProfileStore, (s) => s.insuredProfile);
  const isAuthenticated = Boolean(insuredProfile?.id || insuredProfile?.name || insuredProfile?.username);

  // Show/hide password toggle (eye icon in the password field).
  const [showPw, setShowPw] = useState(false);

  // ── Auth handlers (call API, write to stores) ─────────────────────────
  const onAuthSuccess = async (signInPayload) => {
    loginStore.signInError = null;
    if (isInsuredSessionPayload(signInPayload)) insuredProfileStore.insuredProfile = signInPayload;
    try {
      const fresh = await fetchInsuredSession();
      if (isInsuredSessionPayload(fresh)) {
        insuredProfileStore.insuredProfile = fresh;
        sessionStore.dashView = "dashboard";
        return;
      }
      if (isInsuredSessionPayload(signInPayload)) {
        sessionStore.dashView = "dashboard";
        return;
      }
      throw new Error("Sign-in succeeded but your session was not saved.");
    } catch (e) {
      loginStore.signInError = e.message || "Sign-in succeeded but session was not saved.";
    }
  };

  const handlePasswordSignIn = async () => {
    if (!loginEmail.trim() || !loginPassword || signInLoading) return;
    loginStore.signInError = null;
    loginStore.signInLoading = true;
    try {
      const payload = await postSignIn(loginEmail, loginPassword);
      await onAuthSuccess(payload);
    } catch (e) {
      loginStore.signInError = e.message || "Sign-in failed";
    } finally {
      loginStore.signInLoading = false;
    }
  };

  // Google sign-in (custom button): GSI's renderButton iframe hard-caps at 400px,
  // so we render our own full-width button and trigger the GSI prompt() flow on
  // click. Mirrors the pattern used in RegistrationPage.
  const handleGoogleSignIn = async () => {
    if (googleSignInLoading) return;
    loginStore.signInError = null;
    setGoogleSignInLoading(true);
    try {
      const cfg = await fetchGoogleClientId();
      const clientId = cfg?.clientId;
      if (!clientId) throw new Error("Google sign-in is not configured.");
      await loadGsiScript();
      if (!window.google?.accounts?.id) {
        throw new Error("Google was blocked by your browser. Disable ad-blockers and try again.");
      }

      const credential = await new Promise((resolve, reject) => {
        let done = false;
        const timer = setTimeout(() => {
          if (done) return;
          done = true; reject(new Error("Google sign-in timed out"));
        }, 45_000);
        try {
          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: (resp) => {
              const cred = resp?.credential;
              if (done || !cred) return;
              done = true; clearTimeout(timer); resolve(cred);
            },
          });
          window.google.accounts.id.prompt(() => {});
        } catch (e) {
          if (!done) { done = true; clearTimeout(timer); reject(e); }
        }
      });

      const payload = await postSignInWithGoogle(credential);
      await onAuthSuccess(payload);
    } catch (e) {
      loginStore.signInError = e.message || "Google sign-in failed";
    } finally {
      setGoogleSignInLoading(false);
    }
  };

  // Already signed in? Skip the form — go straight to the dashboard.
  useEffect(() => {
    if (sessionReady && isAuthenticated) {
      sessionStore.dashView = "dashboard";
      navigate("/dashboard", { replace: true });
    }
  }, [sessionReady, isAuthenticated, navigate]);

  if (sessionReady && isAuthenticated) return <Navigate to="/dashboard" replace />;

  // ── "Get a quote" link — clears any in-progress quote state ────────────
  const startFreshQuote = () => {
    quoteFormStore.clear();
    personalStore.clear();
    classificationStore.clear();
    coverageStore.clear();
    questionsStore.clear();
    submissionStore.clear();
    attestStore.clear();
    navigate("/");
  };

  // ── Render ─────────────────────────────────────────────────────────────
  // Mobile shows the shared MedMalGuard header (logo + hamburger) + card.
  // Desktop (>=1024px, via .auth-page CSS in responsive.css) escapes the
  // card, shows the MedMalGuard header (same as the pricing/articles pages),
  // and centers the auth form column.
  return (
    <div className="auth-page">
      {/* MedMalGuard header at every viewport — collapses to logo +
          hamburger on phones, same as the landing page. */}
      <div className="app-header">
        <MedMalGuardHeader />
      </div>

      <div className="auth-body" style={{ flex: 1, padding: "0 18px 18px", overflowY: "auto", display: "flex", flexDirection: "column" }}>
       <div className="auth-card">
        <div style={{ textAlign: "center", margin: "20px 0 4px" }}>
          <div style={{ width: 44, height: 44, borderRadius: "50%", background: BRAND_LIGHT, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><UserIcon /></div>
        </div>
        <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 600, color: "#1a1a1a", textAlign: "center", margin: "8px 0 4px" }}>Welcome back</h2>
        <p style={{ fontSize: 13, color: "#777", textAlign: "center", lineHeight: 1.5, margin: "0 0 20px" }}>Sign in to manage your policy or resume your application.</p>

        <button type="button" onClick={handleGoogleSignIn} disabled={googleSignInLoading} style={{
          width: "100%", height: 40, padding: "0 12px", border: "1px solid #d0d0d0", borderRadius: 10,
          background: "#fff", cursor: googleSignInLoading ? "default" : "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 500, color: "#333",
          opacity: googleSignInLoading ? 0.7 : 1,
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11.96 11.96 0 0 0 0 12c0 1.94.46 3.77 1.28 5.4l3.56-2.77.01-.54z" fill="#FBBC05"/>
            <path d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.09 14.97 0 12 0 7.7 0 3.99 2.47 2.18 6.07l3.66 2.84c.87-2.6 3.3-4.16 6.16-4.16z" fill="#EA4335"/>
          </svg>
          {googleSignInLoading ? "Signing in…" : "Sign in with Google"}
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "16px 0" }}>
          <div style={{ flex: 1, height: 1, background: "#e8e8e6" }} />
          <span style={{ fontSize: 12, color: "#aaa" }}>or sign in with email</span>
          <div style={{ flex: 1, height: 1, background: "#e8e8e6" }} />
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (loginEmail.trim().length > 2 && loginPassword.length >= 6 && !signInLoading) {
              handlePasswordSignIn();
            }
          }}
        >
          <Field label="Email address">
            <TextInput
              value={loginEmail}
              onChange={(v) => { loginStore.loginEmail = v; }}
              placeholder="jane@example.com"
              type="email"
              autoComplete="username"
            />
          </Field>
          <Field label="Password">
            <div style={{ position: "relative" }}>
              <TextInput
                value={loginPassword}
                onChange={(v) => { loginStore.loginPassword = v; }}
                placeholder="Enter your password"
                type={showPw ? "text" : "password"}
                autoComplete="current-password"
                style={{ paddingRight: 40 }}
              />
              {/* type="button" — inside the sign-in form, so a bare button
                  would submit on click. */}
              <button type="button" onClick={() => setShowPw(!showPw)} aria-label={showPw ? "Hide password" : "Show password"} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#999", padding: 0, display: "flex" }}><EyeIcon open={showPw} /></button>
            </div>
          </Field>

          <div style={{ textAlign: "right", margin: "-4px 0 12px" }}>
            <span
              onClick={() => navigate("/forgot-password")}
              style={{ fontSize: 12, color: BRAND, fontWeight: 500, textDecoration: "underline", cursor: "pointer" }}
            >
              Forgot password?
            </span>
          </div>

          <Alert type="error" message={signInError} />

          <button
            type="submit"
            style={dis(btnPrimary, loginEmail.trim().length > 2 && loginPassword.length >= 6 && !signInLoading)}
          >
            {signInLoading ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <div style={{ textAlign: "center", padding: "10px 0 0" }}>
          <span style={{ fontSize: 12, color: "#999" }}>Don't have an account? </span>
          <span onClick={startFreshQuote} style={{ fontSize: 12, color: BRAND, fontWeight: 500, textDecoration: "underline", cursor: "pointer" }}>Get a quote</span>
        </div>
       </div>
      </div>
    </div>
  );
}
